/**
 * Small inline SVG glyphs for the Vedic calendar — no emoji. All use
 * `currentColor` so they inherit text colour, and accept a `size` (px).
 */

interface IconProps {
  size?: number;
  className?: string;
  title?: string;
}

/** Full moon (Pūrṇimā) — a filled disc. */
export function FullMoonIcon({ size = 14, className, title }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} aria-hidden={!title} role={title ? "img" : undefined}>
      {title && <title>{title}</title>}
      <circle cx="8" cy="8" r="6" fill="currentColor" />
    </svg>
  );
}

/** New moon (Amāvāsyā) — a hollow ring with a faint fill. */
export function NewMoonIcon({ size = 14, className, title }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} aria-hidden={!title} role={title ? "img" : undefined}>
      {title && <title>{title}</title>}
      <circle cx="8" cy="8" r="5.4" fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

/** Crescent moon — for moonrise/moonset and generic lunar marks. */
export function MoonIcon({ size = 14, className, title }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} aria-hidden={!title} role={title ? "img" : undefined}>
      {title && <title>{title}</title>}
      <path
        d="M11.2 10.6A5 5 0 0 1 6.2 3a5 5 0 1 0 5 7.6z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Sun — for sunrise/sunset. */
export function SunIcon({ size = 14, className, title }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} aria-hidden={!title} role={title ? "img" : undefined}>
      {title && <title>{title}</title>}
      <circle cx="8" cy="8" r="3.1" fill="currentColor" />
      <g stroke="currentColor" strokeWidth="1.1" strokeLinecap="round">
        <line x1="8" y1="1.2" x2="8" y2="3" />
        <line x1="8" y1="13" x2="8" y2="14.8" />
        <line x1="1.2" y1="8" x2="3" y2="8" />
        <line x1="13" y1="8" x2="14.8" y2="8" />
        <line x1="3.3" y1="3.3" x2="4.6" y2="4.6" />
        <line x1="11.4" y1="11.4" x2="12.7" y2="12.7" />
        <line x1="12.7" y1="3.3" x2="11.4" y2="4.6" />
        <line x1="4.6" y1="11.4" x2="3.3" y2="12.7" />
      </g>
    </svg>
  );
}

/** Location pin — for the "use my location" control. */
export function LocationPinIcon({ size = 14, className, title }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} aria-hidden={!title} role={title ? "img" : undefined}>
      {title && <title>{title}</title>}
      <path
        d="M8 1.4c-2.5 0-4.5 2-4.5 4.5 0 3.1 4 8.2 4.2 8.4a.4.4 0 0 0 .6 0c.2-.2 4.2-5.3 4.2-8.4 0-2.5-2-4.5-4.5-4.5z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="5.9" r="1.7" fill="currentColor" />
    </svg>
  );
}

/** Pick a marker glyph by key/kind. Returns null when no special glyph. */
export function MarkerGlyph({ markerKey, size = 13 }: { markerKey: string; size?: number }) {
  if (markerKey === "purnima") return <FullMoonIcon size={size} />;
  if (markerKey === "amavasya") return <NewMoonIcon size={size} />;
  return null;
}
